// Screen context for the assistant panel.
//
// The panel slides out NEXT TO whatever screen the operator is on, so "what
// are they looking at" is context the AI should have: on the calendar they are
// managing the schedule, on a customer record they are talking about that
// customer. But chat history is TEXT ONLY — tool calls and results are
// discarded after one request — so the context has to ride in the message
// text itself, exactly like @-mention ids do ([mentions: …] in lib/mentions.ts).
// One bracketed line is appended at send time; the panel's message renderer
// strips it again for display. What the model receives and what the operator
// reads differ only in presentation, never in content.
//
// Ids are spelled out in the line (customer_id, booking_id) because an id that
// never reaches the transcript is an id the model invents on a later turn —
// the widget's fabricated-service_id outage is the standing lesson.
//
// ORDER CONTRACT with mentions: screen context is appended BEFORE
// appendMentionContext, so the mentions marker stays terminal (its stripper is
// end-anchored). Display strips mentions first, then screen context.
//
// This module is pure — generator, detector and stripper live together so the
// format has exactly one definition.

export type PageContext =
  | { kind: 'screen'; label: string; hint?: string }
  | { kind: 'schedule'; date: string; view?: 'day' | 'week' | 'resources' }
  | { kind: 'customer'; id: string; name: string }
  | {
      kind: 'booking'
      id: string
      serviceName?: string
      customerName?: string
      customerId?: string
    }

/** The human/model-readable sentence describing what is on screen. */
export function contextLine(ctx: PageContext): string {
  switch (ctx.kind) {
    case 'schedule': {
      const when = ctx.view === 'week' ? `the week of ${ctx.date}` : ctx.date
      return `The operator has the schedule calendar open on ${when}. Bare references like "today's schedule" or "this day" mean ${ctx.date}. The calendar itself is on their screen — answer in text; only render a calendar if they ask to see a different day.`
    }
    case 'customer':
      return `The operator has ${ctx.name}'s customer record open (customer_id: ${ctx.id}). Unqualified references like "this customer", "she", "he" or "they" mean ${ctx.name} — use that id directly instead of searching.`
    case 'booking': {
      const bits = [`booking_id: ${ctx.id}`]
      if (ctx.serviceName) bits.push(`service: ${ctx.serviceName}`)
      if (ctx.customerName) {
        bits.push(
          ctx.customerId
            ? `customer: ${ctx.customerName} (customer_id: ${ctx.customerId})`
            : `customer: ${ctx.customerName}`,
        )
      }
      return `The operator has a booking record open (${bits.join(', ')}). "This booking" or "this session" means it — use those ids directly.`
    }
    case 'screen':
      return `The operator is on the "${ctx.label}" screen${ctx.hint ? ` — ${ctx.hint.toLowerCase()}` : ''}.`
  }
}

/**
 * Stable identity for "has the context changed since the operator last sent
 * a message". The line itself is the identity: two contexts that read the
 * same ARE the same as far as the model is concerned.
 */
export function contextKey(ctx: PageContext | null): string {
  return ctx ? contextLine(ctx) : ''
}

const MARKER_PREFIX = '[Screen context: '
const MARKER_RE = /\n*\[Screen context: [^\n[\]]*\]\s*$/

/**
 * Attach the context line. Returns the text unchanged when there is no
 * context, so an ordinary message never grows a marker.
 */
export function appendScreenContext(text: string, ctx: PageContext | null): string {
  if (!ctx) return text
  return `${text}\n\n${MARKER_PREFIX}${contextLine(ctx)}]`
}

/** Display form: the same message without the machine-added context line. */
export function stripScreenContext(text: string): string {
  return text.replace(MARKER_RE, '')
}

export function hasScreenContext(text: string): boolean {
  return MARKER_RE.test(text)
}
